import { useCallback, useEffect, useRef, useState } from 'react';

import { Select } from 'antd';

import { ErrorFallback, type WidgetErrorProps } from '@/shared/ui/ErrorBoundary';

import { useAvailableWorkers } from '@/features/monitoring';

import styles from './WorkerSelector.module.css';

type WorkerSelectorProps = {
  onWorkerChange: (worker: string) => void;
  title?: string;
};

const Content = ({ onWorkerChange, title }: WorkerSelectorProps) => {
  const { data: workers, isLoading } = useAvailableWorkers();
  const [selectedWorker, setSelectedWorker] = useState<string>('');
  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (workers && workers.length > 0 && !isInitializedRef.current) {
      const initialWorker = workers[0];
      setSelectedWorker(initialWorker);
      onWorkerChange(initialWorker);
      isInitializedRef.current = true;
    }
  }, [workers, onWorkerChange]);

  const handleChange = useCallback(
    (worker: string) => {
      setSelectedWorker(worker);
      onWorkerChange(worker);
    },
    [onWorkerChange]
  );

  if (isLoading) {
    return (
      <div className={styles.container}>
        {title && <h3>{title}</h3>}
        <Select value="" className={styles.select} placeholder="Worker 로딩 중..." disabled loading />
      </div>
    );
  }

  if (!workers || workers.length === 0) {
    return (
      <div className={styles.container}>
        {title && <h3>{title}</h3>}
        <Select value="" className={styles.select} placeholder="사용 가능한 Worker가 없습니다" disabled />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {title && <h3>{title}</h3>}
      <Select
        value={selectedWorker}
        onChange={handleChange}
        className={styles.select}
        options={workers.map((worker: string) => ({
          label: worker,
          value: worker,
        }))}
        placeholder="Worker 선택"
      />
    </div>
  );
};

const Error = ({ title, description, ...props }: WidgetErrorProps) => {
  return <ErrorFallback title={title} description={description} {...props} />;
};

export const WorkerSelector = { Content, Error };
